import { Check, Copy, type LucideIcon } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export const initialsOf = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

export function Avatar({ name, size = 36 }: { name: string; size?: number }) {
  return (
    <span className="avatar" style={{ width: size, height: size, fontSize: size * 0.36 }} aria-hidden="true">
      {initialsOf(name)}
    </span>
  );
}

export function CopyButton({ value, label }: { value: string; label?: string }) {
  const { t } = useTranslation();
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className="btn btn-ghost btn-sm"
      onClick={() =>
        void navigator.clipboard?.writeText(value).then(() => {
          setDone(true);
          setTimeout(() => setDone(false), 1500);
        })
      }
    >
      {done ? <Check size={14} /> : <Copy size={14} />}
      {done ? t('common.copied') : (label ?? t('common.copy'))}
    </button>
  );
}

const PRIORITY_CLASS: Record<string, string> = {
  low: 'badge-neutral',
  normal: 'badge-neutral',
  high: 'badge-warning',
  urgent: 'badge-danger',
};
export function PriorityBadge({ value }: { value: string }) {
  const { t } = useTranslation();
  return (
    <span className={`badge ${PRIORITY_CLASS[value] ?? 'badge-neutral'}`}>{t(`crm.priority.${value}`)}</span>
  );
}

const STATUS_CLASS: Record<string, string> = {
  new: 'badge',
  open: 'badge',
  pending: 'badge-warning',
  resolved: 'badge-success',
  closed: 'badge-neutral',
  won: 'badge-success',
  lost: 'badge-neutral',
  done: 'badge-success',
  cancelled: 'badge-neutral',
  active: 'badge-success',
  error: 'badge-danger',
  disabled: 'badge-neutral',
};
export function StatusBadge({ value, ns }: { value: string; ns: string }) {
  const { t } = useTranslation();
  return <span className={`badge ${STATUS_CLASS[value] ?? 'badge-neutral'}`}>{t(`${ns}.${value}`)}</span>;
}

/**
 * A hint shown on hover and keyboard focus (CSS only, so it costs nothing until used). `side` keeps it inside the
 * screen near edges: "top" for controls at the bottom (the reply box), "start"/"end" to align with that edge.
 */
export function Tip({
  label,
  side = 'bottom',
  children,
  className = '',
}: {
  label: string;
  side?: 'bottom' | 'top' | 'start' | 'end';
  children: ReactNode;
  className?: string;
}) {
  return (
    <span className={`tip tip-${side} ${className}`.trim()} data-tip={label}>
      {children}
    </span>
  );
}

/** A compact action: an icon, its name as tooltip and accessible label, optionally a small count badge. */
export function IconButton({
  icon: Icon,
  label,
  onClick,
  disabled,
  active,
  tone,
  side = 'bottom',
  badge,
  size = 16,
  ...rest
}: {
  icon: LucideIcon;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  active?: boolean;
  tone?: 'brand' | 'danger';
  side?: 'bottom' | 'top' | 'start' | 'end';
  badge?: number | null;
  size?: number;
  'data-testid'?: string;
  'aria-expanded'?: boolean;
  'aria-pressed'?: boolean;
}) {
  return (
    <button
      type="button"
      className={`icon-btn icon-btn-sm tip tip-${side}${active ? ' is-active' : ''}${tone ? ` tone-${tone}` : ''}`}
      aria-label={label}
      data-tip={label}
      onClick={onClick}
      disabled={disabled}
      {...rest}
    >
      <Icon size={size} aria-hidden="true" />
      {badge ? <span className="icon-badge">{badge}</span> : null}
    </button>
  );
}
